import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, TextField, Button,
  CircularProgress, Alert, Snackbar, Chip
} from '@mui/material';
import { Save, Refresh } from '@mui/icons-material';
import axios from 'axios';

const API = 'https://api.bharatdarshan.app/api';

const VENDOR_TYPES = [
  { id: 'hotel', label: 'Sanatani Sadan' },
  { id: 'rojgar', label: 'Rojgar Provider' },
  { id: 'ecommerce', label: 'Marketplace' },
];

const VendorAgreements = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState(0);
  const [agreements, setAgreements] = useState({});
  const [snack, setSnack] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/vendor-agreement`);
      const map = {};
      (r.data?.data || []).forEach(a => { map[a.vendorType] = a; });
      setAgreements(map);
    } catch (e) {
      setSnack({ msg: 'Failed to load agreements', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const current = agreements[VENDOR_TYPES[tab].id] || { title: '', content: '', version: 0 };

  const updateField = (key, val) => {
    const id = VENDOR_TYPES[tab].id;
    setAgreements(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [key]: val, vendorType: id } }));
  };

  const save = async () => {
    const id = VENDOR_TYPES[tab].id;
    const a = agreements[id] || {};
    if (!a.content || !a.content.trim()) {
      setSnack({ msg: 'Content cannot be empty', type: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const r = await axios.put(`${API}/vendor-agreement/${id}`, {
        title: a.title || `${id} Agreement`,
        content: a.content,
      });
      if (r.data?.success) {
        setAgreements(prev => ({ ...prev, [id]: r.data.data }));
        setSnack({ msg: `Saved \u2014 vendors will see new version v${r.data.data.version}`, type: 'success' });
      } else {
        setSnack({ msg: r.data?.message || 'Save failed', type: 'error' });
      }
    } catch (e) {
      setSnack({ msg: e.response?.data?.message || 'Save failed', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Vendor Agreements</Typography>
        <Button startIcon={<Refresh />} onClick={load} variant="outlined" size="small">Refresh</Button>
      </Box>
      <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
        Edit the Terms & Conditions shown to each vendor type during registration. Saving bumps the version number; vendors registering after this will see and accept the new content.
      </Typography>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable">
          {VENDOR_TYPES.map((t, i) => (
            <Tab key={t.id} label={t.label} />
          ))}
        </Tabs>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{VENDOR_TYPES[tab].label}</Typography>
            <Chip size="small" label={`Version v${current.version || 1}`} color="primary" />
            {current.updatedAt && (
              <Typography variant="caption" sx={{ color: '#888' }}>
                Last updated: {new Date(current.updatedAt).toLocaleString()}
              </Typography>
            )}
          </Box>

          <TextField
            label="Title"
            fullWidth
            value={current.title || ''}
            onChange={(e) => updateField('title', e.target.value)}
            sx={{ mb: 2 }}
          />

          <TextField
            label="Agreement Content"
            fullWidth
            multiline
            minRows={20}
            maxRows={40}
            value={current.content || ''}
            onChange={(e) => updateField('content', e.target.value)}
            placeholder="Enter the complete agreement text. Use line breaks for paragraphs."
            sx={{ mb: 2, '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: 13 } }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
              onClick={save}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Agreement'}
            </Button>
          </Box>
        </Paper>
      )}

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack && <Alert severity={snack.type} onClose={() => setSnack(null)}>{snack.msg}</Alert>}
      </Snackbar>
    </Box>
  );
};

export default VendorAgreements;
